import { FC, RefObject, useMemo } from "react";
import {
  CoreFieldValue,
  FieldFormV2,
  FieldFormV2Module,
  FieldV2,
  LoadingV2,
  useFieldSchemaV2FactoryContext,
} from "@ucl/library";
import { FIELD_KEYS } from "@/constants/fieldKeys";
import { JointAccountOwner } from "@/types/JointAccountOwner";
import { createJointAccountOwnerFormBuilder } from "./JointAccountOwnerFormBuilder";
import { FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";
import { PersonInformationComplexValue } from "../ComplexFields/PersonInformationComplexField";
import { GovernmentIdInformationComplexValue } from "../ComplexFields/GovernmentIdInformationComplexField";
import { EmploymentInformationComplexValue } from "../ComplexFields/EmploymentInformationComplexField";
import { DigitalBankingInformationComplexFieldValue } from "../ComplexFields/DigitalBankingInformationComplexField";
import { SecurityQuestionsInformationComplexValue } from "../ComplexFields/SecurityQuestionsInformationComplexField";
import { DebitCardInformationComplexValue } from "../ComplexFields/DebitCardInformationComplexField";

interface JointAccountOwnerFormProps {
  fieldFormModule: RefObject<FieldFormV2Module<JointAccountOwner> | null>;
  formValue: JointAccountOwner;
  setFormValue: (
    newValue: JointAccountOwner,
    newFieldValue: CoreFieldValue,
    fieldProps: any
  ) => void;
  saveFormData: (value: JointAccountOwner) => Promise<void>;
  debitCardInformationRenderKey: string;
  digitalBankingInformationRenderKey: string;
  validationErrors: Record<string, string[]>;
  onBlur?: (field: CoreFieldValue) => void;
  onSecurityQuestionsBlur?: (field: CoreFieldValue) => void;
  validationContextType: FinancialApplicationFormSectionContextType;
  availableFields?: string[];
  shouldHideSecurityQuestions?: boolean;
  shouldAskIfCanProvideGovernmentIdInformation?: boolean;
  shouldAlwaysShowGovernmentIdInformationFields?: boolean;
  shouldShowDigitalBankingFields?: boolean;
  shouldShowDebitCardFields?: boolean;
}

export const JointAccountOwnerForm: FC<JointAccountOwnerFormProps> = ({
  fieldFormModule,
  formValue,
  setFormValue,
  saveFormData,
  debitCardInformationRenderKey,
  digitalBankingInformationRenderKey,
  validationErrors,
  onBlur,
  onSecurityQuestionsBlur,
  validationContextType,
  availableFields,
  shouldHideSecurityQuestions,
  shouldAskIfCanProvideGovernmentIdInformation,
  shouldAlwaysShowGovernmentIdInformationFields,
  shouldShowDigitalBankingFields,
  shouldShowDebitCardFields,
}) => {
  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createJointAccountOwnerFormBuilder(schemaFactory, validationContextType);
  }, [schemaFactory, validationContextType]);

  if (!schemaFactory || !formBuilder || !formValue) {
    return (
      <section className="joint-account-owner-form">
        <LoadingV2 />
      </section>
    );
  }

  const isRelationshipToPrimaryOwnerAvailable =
    !availableFields ||
    availableFields.includes(FIELD_KEYS.JOINT_ACCOUNT_OWNER.RELATIONSHIP_TO_PRIMARY_OWNER);

  return (
    <div className="joint-account-owner-form">
      <FieldFormV2<JointAccountOwner>
        ref={fieldFormModule}
        value={formValue}
        fieldSchemaFormBuilder={formBuilder}
        onFormSubmit={async (value) => {
          await saveFormData(value);
        }}
        onFieldChanged={(newValue, newFieldValue, fieldProps) => {
          setFormValue(newValue, newFieldValue, fieldProps);
        }}
        errors={validationErrors}
        availableFields={availableFields}
      >
        <div className="joint-account-relationship-information">
          {isRelationshipToPrimaryOwnerAvailable && (
            <FieldV2
              fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.RELATIONSHIP_TO_PRIMARY_OWNER}
              handleOnBlur={onBlur}
            />
          )}
        </div>
        <FieldV2<PersonInformationComplexValue>
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.PERSON_INFORMATION}
          handleOnBlur={onBlur}
          complexFieldValue={{
            showHeader: true,
            headerLabel: "Personal Information",
            hiddenFields: ["PHONE_NUMBER", "EMAIL_ADDRESS", "HOME_ADDRESS"],
            className: "personal-information",
          }}
        />
        <FieldV2<GovernmentIdInformationComplexValue>
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.GOVERNMENT_ID_INFORMATION}
          handleOnBlur={onBlur}
          complexFieldValue={{
            showHeader: true,
            headerLabel: "Identification Information",
            shouldAskIfCanProvideGovernmentIdInformation: shouldAskIfCanProvideGovernmentIdInformation,
            canProvideGovernmentIdInformation: formValue.governmentIdInformation?.canProvideGovernmentIdInformation,
            shouldAlwaysShowGovernmentIdInformationFields: shouldAlwaysShowGovernmentIdInformationFields,
          }}
        />
        <FieldV2<PersonInformationComplexValue>
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.PERSON_INFORMATION}
          handleOnBlur={onBlur}
          complexFieldValue={{
            showHeader: true,
            headerLabel: "Contact Information",
            hiddenFields: [
              "FIRST_NAME",
              "MIDDLE_NAME",
              "LAST_NAME",
              "DATE_OF_BIRTH",
              "SOCIAL_SECURITY_NUMBER",
            ],
            className: "contact-information",
            shouldHideHomeAddress: formValue?.homeAddressIsSameAsAccountOpener,
            fieldsBeforeHomeAddress: (
              <FieldV2
                fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.HOME_ADDRESS_IS_SAME_AS_ACCOUNT_OPENER}
                handleOnBlur={onBlur}
              />
            ),
          }}
        />
        <FieldV2<EmploymentInformationComplexValue>
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.EMPLOYMENT_INFORMATION}
          handleOnBlur={onBlur}
          complexFieldValue={{
            // Joint Account Owners do not have an attorney bar number.
            showAttorneyBarNumber: false,
            showHeader: true,
            headerLabel: "Employment Information",
          }}
        />
        <FieldV2<DigitalBankingInformationComplexFieldValue>
          key={digitalBankingInformationRenderKey}
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.DIGITAL_BANKING_INFORMATION}
          handleOnBlur={onBlur}
          complexFieldValue={{
            showHeader: true,
            headerLabel: "Digital Banking",
            shouldShowDigitalBankingFields: shouldShowDigitalBankingFields,
          }}
        />
        <FieldV2<SecurityQuestionsInformationComplexValue>
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.SECURITY_QUESTIONS_INFORMATION}
          handleOnBlur={onSecurityQuestionsBlur ?? onBlur}
          complexFieldValue={{
            showHeader: false,
            shouldHide: shouldHideSecurityQuestions,
          }}
        />
        <FieldV2<DebitCardInformationComplexValue>
          key={debitCardInformationRenderKey}
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.DEBIT_CARD_INFORMATION}
          complexFieldValue={{
            shouldShowBusinessName: false,
            showHeader: true,
            headerLabel: "Debit Card",
            shouldShowDebitCardFields: shouldShowDebitCardFields,
          }}
          handleOnBlur={onBlur}
        />
      </FieldFormV2>
    </div>
  );
};
